import {
  childrenOf,
  descendantsOf,
  type Container,
  type FileEntryExplicit,
} from "@egalregal/shared";

/**
 * The persisted container-kind selection. `null` means **every** kind, including
 * ones that appear later, so a new custom kind participates without a
 * preference migration. An array is an explicit selection (empty = only the
 * viewed container's own media).
 */
export type ContainerKindFilter = string[] | null;

/**
 * One file in the container view's merged media collection, together with the
 * container that owns it. Every action (preview, audio source, remove,
 * re-preview, re-transcribe, transcript writes) must use `container`, never the
 * viewed parent.
 */
export interface ContainerMediaEntry {
  /** The file's owning container: the viewed one or a descendant. */
  container: Container;
  file: FileEntryExplicit;
  /** True for a file below the viewed container. */
  descendant: boolean;
}

/** What the sub-container media preference adds to the viewed container's grid. */
export interface ContainerMediaScope {
  /** The persisted preference; false shows the viewed container's own files only. */
  includeDescendants: boolean;
  /** `null` walks every descendant; an explicit array gates the walk by kind. */
  kinds: ContainerKindFilter;
}

export interface ContainerMediaCollection {
  /** The viewed container's own files first, then matching descendant files. */
  entries: ContainerMediaEntry[];
  /**
   * True when the option is on, the subtree has containers, at least one of
   * them actually holds files, but the gated walk includes none of them (files
   * behind a barrier count as existing) — the grid keeps the viewed media and
   * shows the sub-container empty note.
   */
  subtreeEmpty: boolean;
}

/** The owning container id/version and file id every media write targets. */
export interface ContainerMediaTarget {
  containerId: string;
  fileId: string;
  version: number;
}

/** Resolve the owning container triple once for an entry's actions. */
export function mediaTarget(entry: ContainerMediaEntry): ContainerMediaTarget {
  return {
    containerId: entry.container.id,
    fileId: entry.file.id,
    version: entry.container.version,
  };
}

/**
 * The container kinds present in the viewed container's descendant subtree, in
 * tree order and deduplicated. Custom kinds are included: the filter is derived
 * from what is really stored, not a fixed enum.
 */
export function subtreeKinds(containers: readonly Container[], id: string): string[] {
  const kinds: string[] = [];
  for (const descendant of descendantsOf(containers, id)) {
    if (!kinds.includes(descendant.kind)) kinds.push(descendant.kind);
  }
  return kinds;
}

/**
 * Build the container view's merged media set (container-media 02/03): the
 * viewed container's own photos and memos, then the descendant container files
 * (`Container.files` only — item photos are never read) reached by the
 * kind-gated walk. The viewed container's own media is never filtered.
 *
 * With `kinds === null` every descendant contributes. With an explicit
 * selection the walk only starts when the viewed container's own kind is
 * selected; a selected descendant contributes and is descended into, while a
 * non-selected descendant is a barrier — its files and its whole subtree are
 * excluded. Multiple selected kinds are alternatives. Everything comes from the
 * in-memory readable container set.
 */
export function collectContainerMedia(
  containers: readonly Container[],
  viewed: Container,
  scope: ContainerMediaScope,
): ContainerMediaCollection {
  const entries: ContainerMediaEntry[] = viewed.files.map((file) => ({
    container: viewed,
    file,
    descendant: false,
  }));
  if (!scope.includeDescendants) return { entries, subtreeEmpty: false };

  const descendants = descendantsOf(containers, viewed.id);
  // The note compares included files against every descendant file, including
  // files behind a barrier.
  const subtreeFileCount = descendants.reduce(
    (count, descendant) => count + descendant.files.length,
    0,
  );
  const kindSet = scope.kinds === null ? null : new Set(scope.kinds);
  const walkStarts = kindSet === null || kindSet.has(viewed.kind);
  let includedDescendantFiles = 0;

  if (walkStarts) {
    const walk = (parentId: string) => {
      for (const descendant of childrenOf(containers, parentId)) {
        if (kindSet !== null && !kindSet.has(descendant.kind)) continue;
        for (const file of descendant.files) {
          entries.push({ container: descendant, file, descendant: true });
          includedDescendantFiles += 1;
        }
        walk(descendant.id);
      }
    };
    walk(viewed.id);
  }

  // A subtree of empty boxes has nothing to report: the note is for files the
  // kind filter hides, not for "no files anywhere".
  return {
    entries,
    subtreeEmpty:
      descendants.length > 0 &&
      subtreeFileCount > 0 &&
      includedDescendantFiles === 0,
  };
}
